"use client";

import Link from "@/components/ui/link";
import { useTina, tinaField, coloredText, getIcon, cn } from "@/lib/utils";

import Section from "@/components/Section";
import Image from "next/image";
import Pill from "@/components/Pill";
import SeparatorBanner from "@/components/SeparatorBanner";
import CallToAction from "@/components/CallToAction";

import { Button } from "@/components/ui/button";
import { CircleCheck } from "lucide-react";
import TextTooltip from "@/components/TextTooltip";

export default function MspsInfoPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, solutions, callToActionSeparator, whyUs, callToAction } =
    data.mspsInfo;
  return (
    <div>
      <Header header={header} />
      <Solutions solutions={solutions} />
      <CallToActionSeparator
        callToAction={callToActionSeparator}
        wrapperClassName="!pt-16 lg:!pt-28 !pb-8"
      />
      <WhyUs whyUs={whyUs} />
      <CallToAction callToAction={callToAction} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <Section
      as="header"
      wrapperClassName="bg-gradient-to-b from-background-blackish  to-background-purplish lg:!pt-24 lg:!pb-20"
      className="flex flex-col gap-6 items-center"
    >
      <Pill data-tina-field={tinaField(header, "sectionTitle")}>
        {header?.sectionTitle}
      </Pill>
      <h1
        className="text-center"
        data-tina-field={tinaField(header, "title")}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <TextTooltip
        text={header?.description}
        className="p-xl text-foreground-300 text-center max-w-3xl"
        data-tina-field={tinaField(header, "description")}
      />
      <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
        {header?.problems?.map((item, index) => {
          return (
            <div
              key={index}
              className="card !rounded-xl !px-6 !py-4 bg-background-blackish"
            >
              <TextTooltip
                text={item?.problem}
                className="text-sm md:text-lg text-center text-foreground-300 font-medium whitespace-pre-line"
                data-tina-field={tinaField(header, `problems.${index}.problem`)}
              />
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const Solutions = ({ solutions }) => {
  return (
    <Section
      wrapperClassName="bg-background-purplish !pb-24 lg:!pb-32"
      className="flex flex-col gap-12"
    >
      <div className="flex flex-col gap-5 items-center">
        <Pill data-tina-field={tinaField(solutions, "sectionTitle")}>
          {solutions?.sectionTitle}
        </Pill>
        <h2
          className="text-center"
          data-tina-field={tinaField(solutions, "title")}
        >
          {solutions?.title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
        {solutions?.solutions?.map((item, index) => {
          return (
            <div key={index} className="flex items-start gap-3">
              <CircleCheck className="text-black size-8 fill-primary-300 flex-shrink-0 -translate-y-1" />
              <div className="flex flex-col gap-2">
                <h3
                  className="h5"
                  data-tina-field={tinaField(
                    solutions,
                    `solutions.${index}.title`
                  )}
                >
                  {item?.title}
                </h3>
                <TextTooltip
                  text={item?.description}
                  className="text-foreground-300 p-lg"
                  data-tina-field={tinaField(
                    solutions,
                    `solutions.${index}.description`
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export const CallToActionSeparator = ({ wrapperClassName, callToAction }) => {
  const title = coloredText(callToAction?.title);
  return (
    <Section
      wrapperClassName={wrapperClassName}
      className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-4 card bg-background-purplish !p-12"
    >
      <div className="flex flex-col gap-8 items-center md:items-start">
        <h2
          className="h2 text-balance max-md:text-center"
          data-tina-field={tinaField(callToAction, "title")}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <Button
          data-tina-field={tinaField(callToAction, "button.label")}
          asChild
          size="lg"
        >
          <Link href={callToAction?.button?.link || "/"}>
            {callToAction?.button?.label}
          </Link>
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        <TextTooltip
          text={callToAction?.quote}
          className="text-foreground-300 p-xl max-md:text-center"
          data-tina-field={tinaField(callToAction, "quote")}
        />
        <TextTooltip
          text={callToAction?.quoteAuthor}
          className="uppercase text-foreground-500 max-md:text-center"
          data-tina-field={tinaField(callToAction, "quoteAuthor")}
        />
      </div>
    </Section>
  );
};

const WhyUs = ({ whyUs }) => {
  return (
    <Section
      wrapperClassName="bg-gradient-to-t from-background-purplish to-30% to-background-blackish"
      className="flex flex-col gap-12"
    >
      <div className="flex flex-col gap-5 items-center">
        <Pill data-tina-field={tinaField(whyUs, "sectionTitle")}>
          {whyUs?.sectionTitle}
        </Pill>
        <h2 className="text-center" data-tina-field={tinaField(whyUs, "title")}>
          {whyUs?.title}
        </h2>
      </div>
      <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {whyUs?.reasons?.map((item, index) => {
          const Icon = getIcon(item?.icon);

          return (
            <div
              key={index}
              className="flex flex-col gap-8 card bg-background-blackish"
            >
              {Icon && (
                <div
                  data-tina-field={tinaField(whyUs, `reasons.${index}.icon`)}
                >
                  <Icon className="text-primary-300 size-10 stroke-[1.5px]" />
                </div>
              )}
              <div className="flex flex-col gap-3">
                <h3
                  className="h5"
                  data-tina-field={tinaField(whyUs, `reasons.${index}.title`)}
                >
                  {item?.title}
                </h3>
                <TextTooltip
                  text={item?.description}
                  className="text-foreground-500"
                  data-tina-field={tinaField(
                    whyUs,
                    `reasons.${index}.description`
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};
